import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Save, Loader2, Search, Plus, Trash2, Stethoscope, MapPin, Phone, Landmark, FlaskConical, FileText } from 'lucide-react'
import { z } from 'zod'
import { Controller } from 'react-hook-form'
import { PageHeader } from '@/components/ui/PageHeader'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { AsyncSelect } from '@/components/ui/AsyncSelect'
import { Button } from '@/components/ui/Button'
import { Textarea } from '@/components/ui/Textarea'
import { Toggle } from '@/components/ui/Toggle'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { FormSidemenu, type FormSection } from '@/components/ui/FormSidemenu'
import { DocumentsManager } from '@/components/DocumentsManager'
import { providerContracts } from '@/services/modules/DocumentsService'
import { SpecialtyTestTable, type STRow } from './SpecialtyTestTable'
import { useZodForm } from '@/hooks/useZodForm'
import { useAuthority } from '@/hooks/useAuthority'
import { applyApiErrorsToForm } from '@/lib/api-errors'
import { onValidationFail } from '@/lib/form-helpers'
import { toast } from '@/components/ui/Toast'
import { isValidCpf, isValidCnpj, normalizeForMatch } from '@/lib/validators'
import { brl } from '@/lib/format'
import {
  CredentialingProvidersService, CredentialingStepsService, CredentialingTypesService,
  type ProviderDocumentUsage,
  type CredentialingProvider, type CredentialingStep, type CredentialingProviderType,
  type ProviderSpecialty, type ProviderTest,
} from '@/services/modules/CredentialingService'
import { LookupService, type State, type City, type Bank } from '@/services/modules/LookupService'
import { SalesLinkService } from '@/services/modules/SalesLinkService'
import { SpecialtiesService, type Specialty } from '@/services/modules/SpecialtiesService'
import { TestsService, type Test } from '@/services/modules/TestsService'
import { CurrencyInput } from '@/components/ui/CurrencyInput'

// ============================================================================
// Masks & helpers
// ============================================================================
const onlyDigits = (v: string) => (v ?? '').replace(/\D+/g, '')
function maskCpf(v: string): string {
  const d = onlyDigits(v).slice(0, 11)
  return d.replace(/^(\d{3})(\d)/, '$1.$2').replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3').replace(/\.(\d{3})(\d)/, '.$1-$2')
}
function maskCnpj(v: string): string {
  const d = onlyDigits(v).slice(0, 14)
  return d.replace(/^(\d{2})(\d)/, '$1.$2').replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3').replace(/\.(\d{3})(\d)/, '.$1/$2').replace(/(\d{4})(\d)/, '$1-$2')
}
function maskDoc(type: string, v: string) { return type === 'PESSOA FÍSICA' ? maskCpf(v) : maskCnpj(v) }
function maskCep(v: string): string {
  return onlyDigits(v).slice(0, 8).replace(/^(\d{5})(\d)/, '$1-$2')
}
function maskPhone(v: string): string {
  const d = onlyDigits(v).slice(0, 11)
  if (d.length <= 10) return d.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d)/, '$1-$2')
  return d.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2')
}

const CEP_RE   = /^\d{5}-?\d{3}$/
const PHONE_RE = /^\d{10,11}$/
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const PROVIDER_CONTACT_TYPES = [
  { value: 'E-MAIL',          label: 'E-mail' },
  { value: 'TELEFONE',        label: 'Telefone' },
  { value: 'CELULAR/WHATSAPP', label: 'Celular/WhatsApp' },
  { value: 'APENAS CELULAR',  label: 'Apenas celular' },
  { value: 'APENAS WHATSAPP', label: 'Apenas WhatsApp' },
]

const TRANSACTION_TYPES = [
  { value: 'TRANSFERÊNCIA', label: 'Transferência' },
  { value: 'PIX',          label: 'PIX' },
  { value: 'DEPÓSITO',     label: 'Depósito' },
]
const ACCOUNT_TYPES = [
  { value: 'CORRENTE',  label: 'Conta corrente' },
  { value: 'POUPANÇA',  label: 'Conta poupança' },
  { value: 'SALÁRIO',   label: 'Conta salário' },
  { value: 'CONJUNTA',  label: 'Conta conjunta' },
]

// ============================================================================
// Schema
// ============================================================================
const contactSchema = z.object({
  type:    z.string(),
  contact: z.string(),
}).superRefine((c, ctx) => {
  const trimmed = c.contact.trim()
  if (trimmed === '') {
    ctx.addIssue({ code: 'custom', path: ['contact'], message: 'Informe o contato.' })
    return
  }
  if (c.type === 'E-MAIL') {
    if (!EMAIL_RE.test(trimmed)) ctx.addIssue({ code: 'custom', path: ['contact'], message: 'E-mail inválido.' })
  } else {
    if (!PHONE_RE.test(onlyDigits(trimmed))) ctx.addIssue({ code: 'custom', path: ['contact'], message: 'Telefone deve ter 10 ou 11 dígitos.' })
  }
})

const schema = z.object({
  // Dados
  name:                z.string().trim().min(1, 'Informe o nome do credenciado.').max(200),
  type:                z.enum(['PESSOA FÍSICA', 'PESSOA JURÍDICA']),
  document:            z.string().trim().min(1, 'Informe o CPF/CNPJ.'),
  stepId:              z.string().nullable(),
  typeId:              z.string().nullable(),
  parentId:            z.string().nullable(),
  observations:        z.string().nullable(),
  requiredBankDetails: z.boolean(),

  // Localização
  postalCode: z.string().trim().regex(CEP_RE, 'CEP deve ter 8 dígitos.').or(z.literal('')).nullable(),
  address:    z.string().trim().max(200).or(z.literal('')).nullable(),
  number:     z.string().trim().max(20).or(z.literal('')).nullable(),
  complement: z.string().trim().max(100).or(z.literal('')).nullable(),
  district:   z.string().trim().max(100).or(z.literal('')).nullable(),
  stateId:    z.string().nullable(),
  cityId:     z.string().nullable(),

  // Contatos
  contacts: z.array(contactSchema),

  // Dados bancários
  bankTransaction: z.string().nullable(),
  bankBeneficiary: z.string().nullable(),
  bankPixKey:      z.string().nullable(),
  bankId:          z.string().nullable(),
  bankAgency:      z.string().nullable(),
  bankAccount:     z.string().nullable(),
  bankPersonType:  z.enum(['PESSOA FÍSICA', 'PESSOA JURÍDICA']).nullable(),
  bankDocument:    z.string().nullable(),
  bankAccountType: z.string().nullable(),
}).superRefine((val, ctx) => {
  // Documento do credenciado conforme tipo
  const d = onlyDigits(val.document)
  if (val.type === 'PESSOA FÍSICA') {
    if (d.length !== 11) ctx.addIssue({ code: 'custom', path: ['document'], message: 'CPF deve ter 11 dígitos.' })
    else if (!isValidCpf(d)) ctx.addIssue({ code: 'custom', path: ['document'], message: 'CPF inválido.' })
  } else {
    if (d.length !== 14) ctx.addIssue({ code: 'custom', path: ['document'], message: 'CNPJ deve ter 14 dígitos.' })
    else if (!isValidCnpj(d)) ctx.addIssue({ code: 'custom', path: ['document'], message: 'CNPJ inválido.' })
  }

  // Dados bancários (somente quando requeridos)
  if (val.requiredBankDetails) {
    if (!val.bankTransaction) ctx.addIssue({ code: 'custom', path: ['bankTransaction'], message: 'Selecione o tipo de transação.' })
    if (!val.bankBeneficiary?.trim()) ctx.addIssue({ code: 'custom', path: ['bankBeneficiary'], message: 'Informe o beneficiário.' })

    if (val.bankTransaction === 'PIX') {
      if (!val.bankPixKey?.trim()) ctx.addIssue({ code: 'custom', path: ['bankPixKey'], message: 'Informe a chave PIX.' })
    }
    if (val.bankTransaction === 'DEPÓSITO' || val.bankTransaction === 'TRANSFERÊNCIA') {
      if (!val.bankId) ctx.addIssue({ code: 'custom', path: ['bankId'], message: 'Selecione o banco.' })
      if (!val.bankAgency?.trim()) ctx.addIssue({ code: 'custom', path: ['bankAgency'], message: 'Informe a agência.' })
      if (!val.bankAccount?.trim()) ctx.addIssue({ code: 'custom', path: ['bankAccount'], message: 'Informe a conta.' })
    }
    if (val.bankDocument?.trim()) {
      const bd = onlyDigits(val.bankDocument)
      if (val.bankPersonType === 'PESSOA FÍSICA' && bd.length !== 11) ctx.addIssue({ code: 'custom', path: ['bankDocument'], message: 'CPF deve ter 11 dígitos.' })
      if (val.bankPersonType === 'PESSOA JURÍDICA' && bd.length !== 14) ctx.addIssue({ code: 'custom', path: ['bankDocument'], message: 'CNPJ deve ter 14 dígitos.' })
    }
  }

  if (val.postalCode && val.postalCode !== '' && !CEP_RE.test(val.postalCode)) {
    ctx.addIssue({ code: 'custom', path: ['postalCode'], message: 'CEP deve ter 8 dígitos.' })
  }
})
type FormValues = z.infer<typeof schema>

// ============================================================================
// Page
// ============================================================================
export function CredentialingProviderFormPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id?: string }>()
  const isEdit = !!id
  const [loading, setLoading] = useState(isEdit)
  const [activeSection, setActiveSection] = useState('data')
  const { can } = useAuthority()
  const canSave = can(isEdit ? 'credentialing.providers.edit' : 'credentialing.providers.create')
  const canManageContract = can('credentialing.providers.manage_contract')

  const [steps, setSteps] = useState<CredentialingStep[]>([])
  const [types, setTypes] = useState<CredentialingProviderType[]>([])
  // Rótulo do contrato principal atualmente vinculado (para exibir no AsyncSelect).
  const [parentLabel, setParentLabel] = useState('')
  const [states, setStates] = useState<State[]>([])
  const [cities, setCities] = useState<City[]>([])
  const [banks, setBanks] = useState<Bank[]>([])
  const [cepLoading, setCepLoading] = useState(false)

  // Documento repetido é normal (matriz e filiais compartilham CNPJ), então
  // não bloqueia: mostra quem já usa para o operador reconhecer o caso.
  const [docUsage, setDocUsage] = useState<ProviderDocumentUsage[]>([])

  async function checkDocument(value: string) {
    const digits = value.replace(/\D+/g, '')
    if (digits.length < 11) { setDocUsage([]); return }
    try {
      setDocUsage(await CredentialingProvidersService.documentUsage(digits, id))
    } catch {
      // Aviso é conveniência: falhar aqui não pode atrapalhar o cadastro.
      setDocUsage([])
    }
  }
  // Especialidades / Exames (gerenciados via endpoints — só em edição)
  const [allSpecialties, setAllSpecialties] = useState<Specialty[]>([])
  const [allTests, setAllTests] = useState<Test[]>([])
  const [provSpecialties, setProvSpecialties] = useState<ProviderSpecialty[]>([])
  const [provTests, setProvTests] = useState<ProviderTest[]>([])
  // Especialidades/exames coletados em memória no cadastro (sem id ainda) —
  // enviados junto no create. Em edição usa-se os endpoints add-specialty/add-test.
  const [localSpecs, setLocalSpecs] = useState<LocalST[]>([])
  const [localTests, setLocalTests] = useState<LocalST[]>([])

  const form = useZodForm(schema, {
    defaultValues: {
      name: '', type: 'PESSOA JURÍDICA', document: '',
      stepId: null, typeId: null, parentId: null,
      observations: null, requiredBankDetails: true,
      postalCode: null, address: null, number: null, complement: null, district: null,
      stateId: null, cityId: null,
      contacts: [],
      bankTransaction: null, bankBeneficiary: null, bankPixKey: null,
      bankId: null, bankAgency: null, bankAccount: null,
      bankPersonType: 'PESSOA JURÍDICA', bankDocument: null, bankAccountType: null,
    },
  })
  const errs = form.formState.errors
  const type        = form.watch('type')
  const stateId     = form.watch('stateId')
  const requiresBank = form.watch('requiredBankDetails')
  const bankTransaction = form.watch('bankTransaction')
  const bankPersonType  = form.watch('bankPersonType')

  // Catálogos
  useEffect(() => {
    CredentialingStepsService.list({ size: 100, status: 'ATIVO' }).then(r => setSteps(r.data)).catch(() => setSteps([]))
    CredentialingTypesService.list({ size: 200, status: 'ATIVO' }).then(r => setTypes(r.data)).catch(() => setTypes([]))
    LookupService.states().then(setStates).catch(() => setStates([]))
    LookupService.banks().then(setBanks).catch(() => setBanks([]))
    SpecialtiesService.list({ size: 0, status: 'ATIVO' }).then(r => setAllSpecialties(r.data)).catch(() => setAllSpecialties([]))
    TestsService.list({ size: 0, status: 'ATIVO' }).then(r => setAllTests(r.data)).catch(() => setAllTests([]))
  }, [id])

  // Recarrega especialidades/exames do credenciado (após add/remove)
  async function reloadSpecialtiesTests() {
    if (!id) return
    try {
      const p = await CredentialingProvidersService.show(id)
      setProvSpecialties(p.specialties ?? [])
      setProvTests(p.tests ?? [])
    } catch { /* ignore */ }
  }

  // Cidades por estado
  useEffect(() => {
    if (!stateId) { setCities([]); return }
    LookupService.cities(stateId).then(setCities).catch(() => setCities([]))
  }, [stateId])

  // Carrega provider em edição
  useEffect(() => {
    if (!isEdit || !id) return
    setLoading(true)
    CredentialingProvidersService.show(id)
      .then((p: CredentialingProvider) => {
        const bd = p.bankDetails
        const cityId = p.address?.city?.id ?? null
        const stId = p.address?.city?.state?.id ?? null
        form.reset({
          name: p.name,
          type: (p.type as FormValues['type']) ?? 'PESSOA JURÍDICA',
          document: p.document ? maskDoc(p.type, p.document) : '',
          stepId: p.step?.id ?? null,
          typeId: p.providerType?.id ?? null,
          parentId: p.parent?.id ?? null,
          observations: p.observations ?? null,
          requiredBankDetails: p.requiredBankDetails ?? true,
          postalCode: p.address?.postalCode ? maskCep(p.address.postalCode) : null,
          address: p.address?.address ?? null,
          number: p.address?.number ?? null,
          complement: p.address?.complement ?? null,
          district: p.address?.district ?? null,
          stateId: stId,
          cityId,
          contacts: (p.contacts ?? []).map(c => ({
            type: c.type,
            contact: c.type === 'E-MAIL' ? c.contact : maskPhone(c.contact),
          })),
          bankTransaction: bd?.transaction ?? null,
          bankBeneficiary: bd?.beneficiary ?? null,
          bankPixKey: bd?.pixKey ?? null,
          bankId: bd?.bankId ?? null,
          bankAgency: bd?.agency ?? null,
          bankAccount: bd?.account ?? null,
          bankPersonType: (bd?.type as FormValues['bankPersonType']) ?? 'PESSOA JURÍDICA',
          bankDocument: bd?.document ? maskDoc(bd.type ?? 'PESSOA JURÍDICA', bd.document) : null,
          bankAccountType: bd?.accountType ?? null,
        })
        // Rótulo do contrato principal atual (exibido no AsyncSelect sem recarregar).
        setParentLabel(p.parent?.name ?? '')
        setProvSpecialties(p.specialties ?? [])
        setProvTests(p.tests ?? [])
      })
      .catch(() => toast.error({ title: 'Não encontrado', description: 'Credenciado não localizado.' }))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, id])

  async function lookupCep() {
    const cleaned = onlyDigits(form.getValues('postalCode') ?? '')
    if (cleaned.length !== 8) {
      toast.error({ title: 'CEP inválido', description: 'O CEP precisa ter 8 dígitos.' })
      return
    }
    setCepLoading(true)
    try {
      const data = await SalesLinkService.lookupCep(cleaned)
      if (!data) { toast.error({ title: 'CEP não encontrado', description: 'Verifique o CEP digitado.' }); return }
      form.setValue('address', data.address, { shouldValidate: false })
      form.setValue('district', data.district, { shouldValidate: false })
      const st = states.find(s => s.abbreviation === data.stateAbbr)
      if (!st) { toast.warning({ title: 'Estado não localizado', description: 'Preencha manualmente.' }); return }
      form.setValue('stateId', st.id, { shouldValidate: false })
      let cs = await LookupService.cities(st.id)
      const target = normalizeForMatch(data.cityName)
      let ci = cs.find(c => normalizeForMatch(c.city) === target)
      if (!ci) {
        try {
          const created = await LookupService.findOrCreateCity(data.cityName, st.abbreviation)
          ci = created
          cs = [...cs, created].sort((a, b) => a.city.localeCompare(b.city, 'pt-BR'))
        } catch { toast.error({ title: 'Cidade não encontrada', description: 'Selecione manualmente.' }) }
      }
      setCities(cs)
      if (ci) form.setValue('cityId', ci.id, { shouldValidate: false })
    } catch {
      toast.error({ title: 'Falha ao buscar CEP', description: 'Verifique sua conexão e tente novamente.' })
    } finally {
      setCepLoading(false)
    }
  }

  async function onSubmit(values: FormValues) {
    if (!canSave) return
    try {
      const payload: Record<string, unknown> = {
        name: values.name,
        type: values.type,
        document: onlyDigits(values.document),
        step_id: values.stepId || null,
        type_id: values.typeId || null,
        parent_id: values.parentId || null,
        observations: values.observations || null,
        required_bank_details: values.requiredBankDetails,
        postal_code: values.postalCode ? onlyDigits(values.postalCode) : null,
        address: values.address || null,
        number: values.number || null,
        complement: values.complement || null,
        district: values.district || null,
        city_id: values.cityId || null,
        contacts: values.contacts.map(c => ({
          type: c.type,
          contact: c.type === 'E-MAIL' ? c.contact.trim().toLowerCase() : onlyDigits(c.contact),
        })),
        bank_details: values.requiredBankDetails ? {
          transaction:  values.bankTransaction || null,
          beneficiary:  values.bankBeneficiary || null,
          pix_key:      values.bankTransaction === 'PIX' ? (values.bankPixKey || null) : null,
          bank_id:      values.bankTransaction !== 'PIX' ? (values.bankId || null) : null,
          agency:       values.bankTransaction !== 'PIX' ? (values.bankAgency || null) : null,
          account:      values.bankTransaction !== 'PIX' ? (values.bankAccount || null) : null,
          type:         values.bankPersonType || null,
          document:     values.bankDocument ? onlyDigits(values.bankDocument) : null,
          account_type: values.bankTransaction !== 'PIX' ? (values.bankAccountType || null) : null,
        } : null,
      }
      // No cadastro, especialidades/exames vão junto no payload (sync no backend).
      if (!isEdit) {
        payload.specialties = localSpecs.map(s => ({
          specialty_id: s.itemId,
          price_in_cents: Math.round((s.price ?? 0) * 100),
          discount_value_in_cents: Math.round((s.discount ?? 0) * 100),
          copayment_in_cents: Math.round((s.copay ?? 0) * 100),
          telemedicine: s.telemedicine,
        }))
        payload.tests = localTests.map(t => ({
          test_id: t.itemId,
          price_in_cents: Math.round((t.price ?? 0) * 100),
          discount_value_in_cents: Math.round((t.discount ?? 0) * 100),
          copayment_in_cents: Math.round((t.copay ?? 0) * 100),
        }))
      }
      if (isEdit && id) await CredentialingProvidersService.update(id, payload)
      else              await CredentialingProvidersService.create(payload)

      toast.success({
        title: isEdit ? 'Credenciado atualizado' : 'Credenciado criado',
        description: isEdit ? 'As alterações foram salvas.' : 'O novo credenciado foi cadastrado.',
      })
      navigate('/credentialing/providers')
    } catch (e) {
      applyApiErrorsToForm(e, form.setError, {
        step_id: 'stepId', type_id: 'typeId', parent_id: 'parentId',
        required_bank_details: 'requiredBankDetails', city_id: 'cityId', postal_code: 'postalCode',
      })
    }
  }

  // ===================== Sections =====================
  const dataSection = (
    <SectionShell title="Dados" description="Identificação do credenciado e classificação no processo.">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Input
          label="Nome do credenciado" required wrapperClassName="md:col-span-3"
          {...form.register('name')} error={errs.name?.message}
          placeholder="Clínica Saúde Total"
        />

        <Controller name="type" control={form.control} render={({ field }) => (
          <Select
            label="Tipo" required value={field.value}
            onChange={(e) => {
              field.onChange(e.target.value)
              // re-mascara o documento conforme o novo tipo
              form.setValue('document', maskDoc(e.target.value, form.getValues('document')), { shouldValidate: form.formState.isSubmitted })
            }}
            options={[
              { value: 'PESSOA JURÍDICA', label: 'Pessoa Jurídica' },
              { value: 'PESSOA FÍSICA',   label: 'Pessoa Física' },
            ]}
            searchable={false} clearable={false}
          />
        )} />
        <Controller name="document" control={form.control} render={({ field, fieldState }) => (
          <Input
            label={type === 'PESSOA FÍSICA' ? 'CPF' : 'CNPJ'} required
            value={field.value ?? ''}
            onChange={(e) => field.onChange(maskDoc(type, e.target.value))}
            onBlur={() => { field.onBlur(); void checkDocument(field.value ?? '') }}
            placeholder={type === 'PESSOA FÍSICA' ? '000.000.000-00' : '00.000.000/0000-00'}
            error={fieldState.error?.message}
          />
        )} />

        {docUsage.length > 0 && (
          <div className="md:col-span-3 rounded-md bg-amber-50 px-3 py-2.5 text-xs ring-1 ring-amber-200">
            <p className="font-medium text-amber-900">
              {docUsage.length === 1
                ? 'Já existe um credenciado com este documento'
                : `Já existem ${docUsage.length} credenciados com este documento`}
            </p>
            <p className="mt-0.5 text-amber-800">
              Isso é normal para matriz e filiais. Confira se não é um cadastro repetido antes de salvar.
            </p>
            <ul className="mt-1.5 space-y-0.5 text-amber-900">
              {docUsage.map(u => (
                <li key={u.id}>
                  • {u.name}
                  {u.city ? ` — ${u.city}` : ''}
                  {' '}<span className="text-amber-700">({u.status.toLowerCase()}{u.isBranch ? ', filial' : ''})</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div />

        <Controller name="stepId" control={form.control} render={({ field, fieldState }) => (
          <Select
            label="Etapa" value={field.value ?? ''}
            onChange={(e) => field.onChange(e.target.value || null)}
            placeholder="Selecione a etapa"
            options={steps.map(s => ({ value: s.id, label: s.step }))}
            error={fieldState.error?.message}
          />
        )} />
        <Controller name="typeId" control={form.control} render={({ field, fieldState }) => (
          <Select
            label="Tipo de credenciado" value={field.value ?? ''}
            onChange={(e) => field.onChange(e.target.value || null)}
            placeholder="Selecione o tipo"
            options={[...types]
              .sort((a, b) => (a.path || a.type).localeCompare(b.path || b.type, 'pt-BR', { sensitivity: 'base' }))
              .map(t => ({ value: t.id, label: t.path || t.type }))}
            error={fieldState.error?.message}
          />
        )} />
        <Controller name="parentId" control={form.control} render={({ field }) => (
          <AsyncSelect
            label="Contrato principal"
            value={field.value ?? ''}
            selectedLabel={parentLabel}
            placeholder="Vincular a outro credenciado"
            searchPlaceholder="Buscar por nome ou documento"
            loadOptions={async (q) => {
              const list = await CredentialingProvidersService.mainContractOptions(q, id)
              return list.map(o => ({ value: o.id, label: o.name }))
            }}
            onChange={(v, opt) => { field.onChange(v || null); setParentLabel(opt?.label ?? '') }}
          />
        )} />

        <Textarea
          label="Observações" wrapperClassName="md:col-span-3" rows={3}
          {...form.register('observations')}
          placeholder="Observações internas"
        />

        <Controller name="requiredBankDetails" control={form.control} render={({ field }) => (
          <div className="md:col-span-3 border-t border-gray-100 pt-4">
            <Toggle checked={field.value} onChange={field.onChange} label="Requer dados bancários" />
          </div>
        )} />
      </div>
    </SectionShell>
  )

  const locationSection = (
    <SectionShell title="Localização" description="Digite o CEP e clique na lupa para preencher automaticamente.">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Controller name="postalCode" control={form.control} render={({ field, fieldState }) => (
          <Input
            label="CEP" value={field.value ?? ''}
            onChange={(e) => field.onChange(maskCep(e.target.value))}
            placeholder="00000-000" error={fieldState.error?.message}
            wrapperClassName="md:col-span-1"
            actionRight={(
              <button type="button" onClick={lookupCep} disabled={cepLoading}
                aria-label="Buscar CEP" title="Buscar CEP"
                className="cursor-pointer inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-500 hover:text-brand-600 hover:bg-gray-100 transition disabled:opacity-50 disabled:cursor-not-allowed">
                {cepLoading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
              </button>
            )}
          />
        )} />
        <Input label="Logradouro" wrapperClassName="md:col-span-2" {...form.register('address')}    error={errs.address?.message} />
        <Input label="Número"      wrapperClassName="md:col-span-1" {...form.register('number')}     error={errs.number?.message} />
        <Input label="Complemento" wrapperClassName="md:col-span-1" {...form.register('complement')} error={errs.complement?.message} placeholder="Sala, andar" />
        <Input label="Bairro"      wrapperClassName="md:col-span-1" {...form.register('district')}   error={errs.district?.message} />
        <Controller name="stateId" control={form.control} render={({ field, fieldState }) => (
          <Select
            label="Estado" value={field.value ?? ''}
            onChange={(e) => { field.onChange(e.target.value || null); form.setValue('cityId', null, { shouldValidate: false }) }}
            placeholder="UF"
            options={states.map(s => ({ value: s.id, label: s.abbreviation }))}
            error={fieldState.error?.message} wrapperClassName="md:col-span-1"
          />
        )} />
        <Controller name="cityId" control={form.control} render={({ field, fieldState }) => (
          <Select
            label="Cidade" value={field.value ?? ''}
            onChange={(e) => field.onChange(e.target.value || null)}
            placeholder={stateId ? 'Cidade' : 'Selecione o estado primeiro'}
            disabled={!stateId}
            options={cities.map(c => ({ value: c.id, label: c.city }))}
            error={fieldState.error?.message} wrapperClassName="md:col-span-2"
          />
        )} />
      </div>
    </SectionShell>
  )

  const contactsSection = (
    <ContactsManager
      value={form.watch('contacts')}
      onChange={(next) => form.setValue('contacts', next, { shouldValidate: form.formState.isSubmitted })}
      errors={errs.contacts as unknown as Array<Record<string, { message?: string }>> | undefined}
      forceShowErrors={form.formState.isSubmitted}
    />
  )

  const bankSection = (
    <SectionShell title="Dados bancários" description="Como o credenciado receberá os repasses.">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Controller name="bankTransaction" control={form.control} render={({ field, fieldState }) => (
          <Select
            label="Tipo de transação" required value={field.value ?? ''}
            onChange={(e) => field.onChange(e.target.value || null)}
            placeholder="Selecione"
            options={TRANSACTION_TYPES}
            searchable={false}
            error={fieldState.error?.message}
          />
        )} />
        <Input
          label="Beneficiário" required wrapperClassName="md:col-span-2"
          {...form.register('bankBeneficiary')} error={errs.bankBeneficiary?.message}
          placeholder="Nome do titular da conta"
        />

        {bankTransaction === 'PIX' && (
          <Input
            label="Chave PIX" required wrapperClassName="md:col-span-3"
            {...form.register('bankPixKey')} error={errs.bankPixKey?.message}
            placeholder="CPF, e-mail, telefone ou chave aleatória"
          />
        )}

        {(bankTransaction === 'DEPÓSITO' || bankTransaction === 'TRANSFERÊNCIA') && (
          <>
            <Controller name="bankId" control={form.control} render={({ field, fieldState }) => (
              <Select
                label="Banco" required value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value || null)}
                placeholder="Selecione o banco"
                options={banks.map(b => ({ value: b.id, label: `${b.code} — ${b.name}` }))}
                error={fieldState.error?.message}
              />
            )} />
            <Input label="Agência" required {...form.register('bankAgency')} error={errs.bankAgency?.message} placeholder="0001" />
            <Input label="Conta" required {...form.register('bankAccount')} error={errs.bankAccount?.message} placeholder="12345-6" />
            <Controller name="bankAccountType" control={form.control} render={({ field, fieldState }) => (
              <Select
                label="Tipo de conta" value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value || null)}
                placeholder="Selecione"
                options={ACCOUNT_TYPES}
                searchable={false}
                error={fieldState.error?.message}
              />
            )} />
          </>
        )}

        {bankTransaction && (
          <>
            <Controller name="bankPersonType" control={form.control} render={({ field }) => (
              <Select
                label="Titular (PF/PJ)" value={field.value ?? ''}
                onChange={(e) => {
                  field.onChange((e.target.value || null) as FormValues['bankPersonType'])
                  form.setValue('bankDocument', maskDoc(e.target.value, form.getValues('bankDocument') ?? ''), { shouldValidate: form.formState.isSubmitted })
                }}
                options={[
                  { value: 'PESSOA JURÍDICA', label: 'Pessoa Jurídica' },
                  { value: 'PESSOA FÍSICA',   label: 'Pessoa Física' },
                ]}
                searchable={false} clearable={false}
              />
            )} />
            <Controller name="bankDocument" control={form.control} render={({ field, fieldState }) => (
              <Input
                label={bankPersonType === 'PESSOA FÍSICA' ? 'CPF do titular' : 'CNPJ do titular'}
                wrapperClassName="md:col-span-2"
                value={field.value ?? ''}
                onChange={(e) => field.onChange(maskDoc(bankPersonType ?? 'PESSOA JURÍDICA', e.target.value))}
                placeholder={bankPersonType === 'PESSOA FÍSICA' ? '000.000.000-00' : '00.000.000/0000-00'}
                error={fieldState.error?.message}
              />
            )} />
          </>
        )}
      </div>
    </SectionShell>
  )

  // Contagem de erros por seção (para badge no sidemenu)
  const dataErr     = !!(errs.name || errs.document || errs.type)
  const locationErr = !!(errs.postalCode || errs.address || errs.number || errs.complement || errs.district || errs.stateId || errs.cityId)
  const contactsErr = !!errs.contacts
  const bankErr     = !!(errs.bankTransaction || errs.bankBeneficiary || errs.bankPixKey || errs.bankId || errs.bankAgency || errs.bankAccount || errs.bankDocument)

  const specialtiesSection = (
    <SpecialtyTestManager
      kind="specialty"
      providerId={id ?? ''}
      options={[...allSpecialties].sort((a, b) => a.specialty.localeCompare(b.specialty, 'pt-BR', { sensitivity: 'base' })).map(s => ({ value: s.id, label: s.specialty }))}
      rows={provSpecialties.map(s => ({ pivotId: s.pivotId, name: s.specialty, formatted: s.formatted, telemedicine: s.telemedicine }))}
      usedIds={provSpecialties.map(s => s.id)}
      onChange={reloadSpecialtiesTests}
    />
  )
  const testsSection = (
    <SpecialtyTestManager
      kind="test"
      providerId={id ?? ''}
      options={[...allTests].sort((a, b) => a.test.localeCompare(b.test, 'pt-BR', { sensitivity: 'base' })).map(t => ({ value: t.id, label: t.test }))}
      rows={provTests.map(t => ({ pivotId: t.pivotId, name: t.test, formatted: t.formatted }))}
      usedIds={provTests.map(t => t.id)}
      onChange={reloadSpecialtiesTests}
    />
  )

  // Versões de cadastro (estado local — enviadas no payload do create)
  const localSpecialtiesSection = (
    <LocalSpecialtyTestManager
      kind="specialty"
      options={[...allSpecialties].sort((a, b) => a.specialty.localeCompare(b.specialty, 'pt-BR', { sensitivity: 'base' })).map(s => ({ value: s.id, label: s.specialty }))}
      rows={localSpecs}
      onChange={setLocalSpecs}
    />
  )
  const localTestsSection = (
    <LocalSpecialtyTestManager
      kind="test"
      options={[...allTests].sort((a, b) => a.test.localeCompare(b.test, 'pt-BR', { sensitivity: 'base' })).map(t => ({ value: t.id, label: t.test }))}
      rows={localTests}
      onChange={setLocalTests}
    />
  )

  const sections: FormSection[] = [
    { key: 'data',     label: 'Dados',       icon: Stethoscope, content: dataSection,     hasError: dataErr,     errorCount: dataErr ? 1 : 0 },
    { key: 'location', label: 'Localização', icon: MapPin,      content: locationSection, hasError: locationErr, errorCount: locationErr ? 1 : 0 },
    { key: 'contacts', label: 'Contatos',    icon: Phone,       content: contactsSection, hasError: contactsErr, errorCount: contactsErr ? 1 : 0 },
    ...(requiresBank ? [{
      key: 'bank', label: 'Dados bancários', icon: Landmark, content: bankSection,
      hasError: bankErr, errorCount: bankErr ? 1 : 0,
    } as FormSection] : []),
    // Especialidades e Exames — em edição via endpoints; no cadastro via estado local.
    ...(isEdit ? [
      { key: 'specialties', label: `Especialidades (${provSpecialties.length})`, icon: Stethoscope, content: specialtiesSection } as FormSection,
      { key: 'tests',       label: `Exames (${provTests.length})`,               icon: FlaskConical, content: testsSection } as FormSection,
    ] : [
      { key: 'specialties', label: `Especialidades (${localSpecs.length})`, icon: Stethoscope, content: localSpecialtiesSection } as FormSection,
      { key: 'tests',       label: `Exames (${localTests.length})`,         icon: FlaskConical, content: localTestsSection } as FormSection,
    ]),
    ...(isEdit ? [{
      key: 'contracts', label: 'Contratos', icon: FileText,
      content: (
        <DocumentsManager
          service={providerContracts(id!)}
          enabled={isEdit}
          title="Contratos"
          description="Veja o contrato enviado e anexe novos contratos do credenciado."
          accept=".pdf"
          readOnly={!canManageContract}
        />
      ),
    } as FormSection] : []),
  ]

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 size={28} className="animate-spin text-gray-400" /></div>
  }

  return (
    <form noValidate onSubmit={form.handleSubmit(onSubmit, (e) => {
      onValidationFail(e)
      if (dataErr) setActiveSection('data')
      else if (locationErr) setActiveSection('location')
      else if (contactsErr) setActiveSection('contacts')
      else if (bankErr) setActiveSection('bank')
    })}>
      <PageHeader
        title={isEdit ? 'Editar credenciado' : 'Novo credenciado'}
        subtitle="Médicos, clínicas, laboratórios e dentistas"
        crumbs={[{ label: 'Credenciamento' }, { label: 'Credenciados', to: '/credentialing/providers' }, { label: isEdit ? 'Editar' : 'Novo' }]}
        backTo="/credentialing/providers"
        actions={<Button type="submit" loading={form.formState.isSubmitting} iconLeft={<Save size={14} />}
          disabled={!canSave}
          title={canSave ? undefined : `Sem permissão para ${isEdit ? 'editar' : 'criar'} credenciados`}>Salvar</Button>}
      />
      <FormSidemenu sections={sections} activeKey={activeSection} onChange={setActiveSection} />
    </form>
  )
}

// ============================================================================
// SectionShell + ContactsManager (espelham Companies/Form)
// ============================================================================
function SectionShell({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-md ring-1 ring-gray-200 p-5 sm:p-6">
      <div className="mb-4">
        <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
        {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      </div>
      {children}
    </div>
  )
}

interface ProviderContact { type: string; contact: string }

function ContactsManager({ value, onChange, errors, forceShowErrors }: {
  value: ProviderContact[]
  onChange: (next: ProviderContact[]) => void
  errors?: Array<Record<string, { message?: string }>>
  forceShowErrors?: boolean
}) {
  const [deletingIndex, setDeletingIndex] = useState<number | null>(null)
  const [touched, setTouched] = useState<Set<number>>(new Set())

  function add() { onChange([...value, { type: 'E-MAIL', contact: '' }]) }
  function confirmRemove() {
    if (deletingIndex === null) return
    onChange(value.filter((_, idx) => idx !== deletingIndex))
    setDeletingIndex(null)
  }
  function update(i: number, patch: Partial<ProviderContact>) {
    onChange(value.map((c, idx) => idx === i ? { ...c, ...patch } : c))
  }
  function maskedFor(type: string, raw: string) { return type === 'E-MAIL' ? raw : maskPhone(raw) }
  function markTouched(i: number) {
    setTouched(prev => { if (prev.has(i)) return prev; const n = new Set(prev); n.add(i); return n })
  }

  return (
    <SectionShell title="Contatos" description="Adicione um ou mais canais de contato (e-mail, telefone, celular, WhatsApp).">
      {value.length === 0 ? (
        <p className="text-[13px] text-gray-500 mb-3">Nenhum contato adicionado.</p>
      ) : (
        <ul className="space-y-2 mb-3">
          {value.map((c, i) => {
            const showErr = forceShowErrors || touched.has(i)
            const itemErr = showErr ? errors?.[i] : undefined
            const placeholder = c.type === 'E-MAIL' ? 'contato@empresa.com.br' : '(11) 99999-9999'
            return (
              <li key={i} className="bg-gray-50 ring-1 ring-gray-200 rounded-md p-3">
                <div className="grid grid-cols-1 md:grid-cols-[220px_1fr_auto] md:items-end gap-3">
                  <Select
                    label="Tipo" required value={c.type}
                    onChange={(e) => update(i, { type: e.target.value, contact: '' })}
                    options={PROVIDER_CONTACT_TYPES}
                    searchable={false} clearable={false}
                  />
                  <Input
                    label="Contato" required value={c.contact}
                    onChange={(e) => update(i, { contact: maskedFor(c.type, e.target.value) })}
                    onBlur={() => markTouched(i)}
                    placeholder={placeholder}
                    error={itemErr?.contact?.message}
                  />
                  <button type="button" onClick={() => setDeletingIndex(i)} aria-label="Remover contato"
                    className="cursor-pointer inline-flex items-center justify-center w-9 h-9 rounded-md text-rose-500 hover:bg-rose-50 transition self-end mb-[2px]">
                    <Trash2 size={14} />
                  </button>
                </div>
              </li>
            )
          })}
        </ul>
      )}
      <Button type="button" size="sm" variant="ghost" onClick={add} iconLeft={<Plus size={12} />}>
        Adicionar contato
      </Button>

      <ConfirmDialog
        open={deletingIndex !== null}
        onClose={() => setDeletingIndex(null)}
        onConfirm={confirmRemove}
        title="Remover contato?"
        description="Tem certeza que deseja remover este contato? A ação só será aplicada quando você salvar."
        confirmLabel="Remover"
        variant="danger"
      />
    </SectionShell>
  )
}

// =============================================================================
// SpecialtyTestManager — formulário de adição + tabela (SpecialtyTestTable)
// (edição apenas; usa endpoints add-specialty / add-test)
// =============================================================================
function SpecialtyTestManager({ kind, providerId, options, rows, usedIds, onChange }: {
  kind: 'specialty' | 'test'
  providerId: string
  options: { value: string; label: string }[]
  rows: STRow[]
  usedIds: string[]
  onChange: () => void | Promise<void>
}) {
  const isSpec = kind === 'specialty'
  const { can } = useAuthority()
  // add-specialty → manage_specialties; add-test → manage_tests.
  const canAdd = can(isSpec ? 'credentialing.providers.manage_specialties' : 'credentialing.providers.manage_tests')
  const [itemId, setItemId] = useState('')
  const [price, setPrice] = useState<number | null>(0)
  const [discount, setDiscount] = useState<number | null>(0)
  const [copay, setCopay] = useState<number | null>(0)
  const [online, setOnline] = useState(false)
  const [saving, setSaving] = useState(false)

  // Não pode escolher o que já está cadastrado.
  const used = new Set(usedIds)
  const available = options.filter(o => !used.has(o.value))

  function reset() {
    setItemId(''); setPrice(0); setDiscount(0); setCopay(0); setOnline(false)
  }

  async function add() {
    if (!itemId) { toast.error({ title: isSpec ? 'Selecione a especialidade' : 'Selecione o exame', description: 'Escolha um item para adicionar.' }); return }
    setSaving(true)
    try {
      const payload = {
        price_in_cents: Math.round((price ?? 0) * 100),
        discount_value_in_cents: Math.round((discount ?? 0) * 100),
        copayment_in_cents: Math.round((copay ?? 0) * 100),
      }
      if (isSpec) {
        await CredentialingProvidersService.addSpecialty(providerId, { specialty_id: itemId, telemedicine: online, ...payload })
      } else {
        await CredentialingProvidersService.addTest(providerId, { test_id: itemId, ...payload })
      }
      toast.success({ title: 'Adicionado', description: isSpec ? 'Especialidade incluída.' : 'Exame incluído.' })
      reset()
      await onChange()
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Erro ao adicionar.'
      toast.error({ title: 'Não foi possível adicionar', description: msg })
    } finally { setSaving(false) }
  }

  return (
    <SectionShell
      title={isSpec ? 'Especialidades' : 'Exames'}
      description={isSpec
        ? 'Especialidades atendidas pelo credenciado, com valores e coparticipação.'
        : 'Exames realizados pelo credenciado, com valores e coparticipação.'}
    >
      {canAdd && (
      <div className="bg-gray-50 ring-1 ring-gray-200 rounded-md p-4 mb-4">
        {/* Linha 1: item + (online) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Select
            label={isSpec ? 'Especialidade' : 'Exame'} required
            value={itemId} onChange={(e) => setItemId(e.target.value)}
            placeholder={isSpec ? 'Selecione a especialidade' : 'Selecione o exame'}
            options={available}
            wrapperClassName={isSpec ? '' : 'md:col-span-2'}
          />
          {isSpec && (
            <div className="flex items-end pb-1">
              <Toggle checked={online} onChange={setOnline} label="Faz atendimento online?" />
            </div>
          )}
        </div>
        {/* Linha 2: Valor | Desconto | Coparticipação (mesma linha) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
          <CurrencyInput label="Valor" value={price} onChange={setPrice} />
          <CurrencyInput label="Desconto Ampla+" value={discount} onChange={setDiscount} />
          <CurrencyInput label="Coparticipação coberta pela Ampla+" value={copay} onChange={setCopay} />
        </div>
        <div className="mt-3 flex justify-end">
          <Button type="button" onClick={add} loading={saving} iconLeft={<Plus size={12} />} disabled={available.length === 0}>
            {isSpec ? 'Adicionar especialidade' : 'Adicionar exame'}
          </Button>
        </div>
        {available.length === 0 && options.length > 0 && (
          <p className="mt-2 text-xs text-gray-400">Todos os itens cadastrados já foram adicionados.</p>
        )}
      </div>
      )}

      <SpecialtyTestTable kind={kind} providerId={providerId} rows={rows} onChanged={onChange} />
    </SectionShell>
  )
}

// =============================================================================
// LocalSpecialtyTestManager — versão de CADASTRO (estado local em memória).
// Coleta os itens e seus valores; o ProviderForm envia tudo no payload do create.
// =============================================================================
interface LocalST { itemId: string; name: string; price: number; discount: number; copay: number; telemedicine: boolean }

function LocalSpecialtyTestManager({ kind, options, rows, onChange }: {
  kind: 'specialty' | 'test'
  options: { value: string; label: string }[]
  rows: LocalST[]
  onChange: (next: LocalST[]) => void
}) {
  const isSpec = kind === 'specialty'
  const [itemId, setItemId] = useState('')
  const [price, setPrice] = useState<number | null>(0)
  const [discount, setDiscount] = useState<number | null>(0)
  const [copay, setCopay] = useState<number | null>(0)
  const [online, setOnline] = useState(false)

  const used = new Set(rows.map(r => r.itemId))
  const available = options.filter(o => !used.has(o.value))

  function add() {
    if (!itemId) {
      toast.error({ title: isSpec ? 'Selecione a especialidade' : 'Selecione o exame', description: 'Escolha um item para adicionar.' })
      return
    }
    const name = options.find(o => o.value === itemId)?.label ?? ''
    onChange([...rows, { itemId, name, price: price ?? 0, discount: discount ?? 0, copay: copay ?? 0, telemedicine: online }])
    setItemId(''); setPrice(0); setDiscount(0); setCopay(0); setOnline(false)
  }
  function remove(i: number) { onChange(rows.filter((_, idx) => idx !== i)) }

  return (
    <SectionShell
      title={isSpec ? 'Especialidades' : 'Exames'}
      description={isSpec
        ? 'Especialidades atendidas pelo credenciado, com valores e coparticipação.'
        : 'Exames realizados pelo credenciado, com valores e coparticipação.'}
    >
      <div className="bg-gray-50 ring-1 ring-gray-200 rounded-md p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Select
            label={isSpec ? 'Especialidade' : 'Exame'} required
            value={itemId} onChange={(e) => setItemId(e.target.value)}
            placeholder={isSpec ? 'Selecione a especialidade' : 'Selecione o exame'}
            options={available}
            wrapperClassName={isSpec ? '' : 'md:col-span-2'}
          />
          {isSpec && (
            <div className="flex items-end pb-1">
              <Toggle checked={online} onChange={setOnline} label="Faz atendimento online?" />
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
          <CurrencyInput label="Valor" value={price} onChange={setPrice} />
          <CurrencyInput label="Desconto Ampla+" value={discount} onChange={setDiscount} />
          <CurrencyInput label="Coparticipação coberta pela Ampla+" value={copay} onChange={setCopay} />
        </div>
        <div className="mt-3 flex justify-end">
          <Button type="button" onClick={add} iconLeft={<Plus size={12} />} disabled={available.length === 0}>
            {isSpec ? 'Adicionar especialidade' : 'Adicionar exame'}
          </Button>
        </div>
        {available.length === 0 && options.length > 0 && (
          <p className="mt-2 text-xs text-gray-400">Todos os itens cadastrados já foram adicionados.</p>
        )}
      </div>

      {rows.length === 0 ? (
        <p className="text-[13px] text-gray-500">Nenhum item adicionado.</p>
      ) : (
        <ul className="divide-y divide-gray-100 rounded-md ring-1 ring-gray-200">
          {rows.map((r, i) => (
            <li key={r.itemId} className="flex items-center justify-between gap-3 px-3 py-2">
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-800 break-words">
                  {r.name}
                  {isSpec && r.telemedicine && <span className="ml-2 text-[10px] uppercase tracking-wide rounded bg-brand-50 text-brand-700 px-1.5 py-0.5">online</span>}
                </div>
                <div className="text-xs text-gray-500">
                  Valor {brl(r.price)} · Desc. {brl(r.discount)} · Copart. {brl(r.copay)}
                </div>
              </div>
              <button type="button" onClick={() => remove(i)} aria-label="Remover"
                className="cursor-pointer inline-flex items-center justify-center w-8 h-8 rounded-md text-rose-500 hover:bg-rose-50 transition shrink-0">
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </SectionShell>
  )
}
